import { useState } from 'react'
import type { BugReport } from '@/hooks/use-bug-reports'
import { useUpdateBugReport } from '@/hooks/use-bug-reports'

const STATUS_ACTIONS = [
  { status: 'reviewing', label: 'Mark Reviewing', color: 'bg-blue-500 hover:bg-blue-600' },
  { status: 'accepted', label: 'Accept', color: 'bg-green-500 hover:bg-green-600' },
  { status: 'duplicate', label: 'Mark Duplicate', color: 'bg-gray-500 hover:bg-gray-600' },
  { status: 'rejected', label: 'Reject', color: 'bg-red-500 hover:bg-red-600' },
  { status: 'fixed', label: 'Mark Fixed', color: 'bg-purple-500 hover:bg-purple-600' },
] as const

interface BugReportDetailPanelProps {
  report: BugReport
  onClose: () => void
}

export function BugReportDetailPanel({
  report,
  onClose,
}: BugReportDetailPanelProps): React.JSX.Element {
  const [notes, setNotes] = useState(report.admin_notes ?? '')
  const updateReport = useUpdateBugReport()

  function handleStatusChange(status: string): void {
    updateReport.mutate({ id: report.id, status, admin_notes: notes || undefined })
  }

  function handleAwardFreeMonth(): void {
    updateReport.mutate({
      id: report.id,
      status: 'accepted',
      rewarded: true,
      admin_notes: notes || undefined,
    })
  }

  function handleSaveNotes(): void {
    updateReport.mutate({ id: report.id, admin_notes: notes })
  }

  const r2BaseUrl = import.meta.env.VITE_R2_PUBLIC_URL ?? ''

  return (
    <tr>
      <td colSpan={7} className="bg-surface-1 px-4 py-4">
        <div className="space-y-4">
          {/* Description */}
          <div>
            <h4 className="text-xs font-medium uppercase tracking-wider text-text-secondary">
              Description
            </h4>
            <p className="mt-1 whitespace-pre-wrap text-sm text-text-primary">
              {report.description}
            </p>
          </div>

          {/* Screenshot */}
          {report.screenshot_key && (
            <div>
              <h4 className="text-xs font-medium uppercase tracking-wider text-text-secondary">
                Screenshot
              </h4>
              <img
                src={`${r2BaseUrl}/${report.screenshot_key}`}
                alt="Bug report screenshot"
                className="mt-1 max-h-64 rounded-lg border border-surface-3 object-contain"
              />
            </div>
          )}

          {/* Admin notes */}
          <div>
            <h4 className="text-xs font-medium uppercase tracking-wider text-text-secondary">
              Admin Notes
            </h4>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Add notes about this report..."
              className="mt-1 w-full rounded-lg border border-surface-3 px-3 py-2 text-sm focus:border-admin-500 focus:outline-none"
            />
            <button
              type="button"
              onClick={handleSaveNotes}
              disabled={updateReport.isPending}
              className="mt-1 rounded-lg border border-surface-3 px-3 py-1.5 text-xs font-medium text-text-primary hover:bg-surface-2 disabled:opacity-50"
            >
              Save Notes
            </button>
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap items-center gap-2">
            {STATUS_ACTIONS.map((action) => (
              <button
                type="button"
                key={action.status}
                onClick={() => handleStatusChange(action.status)}
                disabled={report.status === action.status || updateReport.isPending}
                className={`rounded-lg px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40 ${action.color}`}
              >
                {action.label}
              </button>
            ))}
            <button
              type="button"
              onClick={handleAwardFreeMonth}
              disabled={report.rewarded || updateReport.isPending}
              className="rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-600 disabled:opacity-40"
            >
              {report.rewarded ? 'Already Rewarded' : 'Award Month Free'}
            </button>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="text-xs text-text-secondary hover:text-text-primary"
          >
            Close details
          </button>
        </div>
      </td>
    </tr>
  )
}
